import { ToolDialog, ToolSummary, ToolSearch, ToolButton, ToolEmpty, ToolFilters } from "./ToolWorkspace";
import { HeaderIcon } from "./AppHeader";
import { useState, useEffect, useRef } from "react";
import { toast } from "../hooks/useToast";
import { C } from "../constants/theme";
import { fbList, fbAdd, fbUpdate, fbDelete } from "../hooks/useFirebase";
import { useConfirm } from "../hooks/useConfirm";
import { imagemParaBase64 } from "../utils/extract3mf";
import { buscarFotoMaterial } from "../utils/buscarFotoWeb";

import { Inp } from "./ui/Inp";
import { Sel } from "./ui/Sel";
import { Btn } from "./ui/Btn";

const UNIDADES = ["un","par","m","cm","g","kg","ml","L","pct"];

// Campo de foto reutilizado no card de criação e no popup de edição: preview +
// botão que busca uma foto de referência na internet (Wikimedia Commons, sem
// precisar de chave de API) pelo nome digitado + upload manual de arquivo.
function CampoFoto({ nome, foto, onChange }) {
  const fileRef = useRef();
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const buscar = async () => {
    if (!nome?.trim()) { toast("Digite o nome do material primeiro", "warning"); return; }
    setBuscando(true);
    const url = await buscarFotoMaterial(nome);
    setBuscando(false);
    if (url) onChange(url); else toast("Nenhuma foto encontrada pra esse nome. Tente enviar uma foto sua.", "warning");
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnviando(true);
    const base64 = await imagemParaBase64(file);
    setEnviando(false);
    if (base64) onChange(base64);
    e.target.value = "";
  };
  return (
    <div>
      <div style={{ color:C.muted, fontSize:12, fontWeight:600, letterSpacing:1, marginBottom:6 }}>FOTO</div>
      <div style={{ display:"flex", gap:10, alignItems:"center" }}>
        <div style={{ width:56, height:56, borderRadius:10, background:C.bg, border:"1px solid "+C.border, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", flexShrink:0 }}>
          {foto ? <img src={foto} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <span style={{ fontSize:22, opacity:0.4 }}>🧩</span>}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:6, flex:1 }}>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <Btn small outline color="#e879f9" onClick={buscar} disabled={buscando}>{buscando?"⏳ Buscando...":"🔍 Buscar na internet"}</Btn>
            <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={upload} />
            <Btn small outline onClick={()=>fileRef.current.click()} disabled={enviando}>{enviando?"⏳ Enviando...":"📎 Enviar foto"}</Btn>
          </div>
          {foto && <button onClick={()=>onChange("")} style={{ background:"none", border:"none", color:C.danger, fontSize:11, cursor:"pointer", textAlign:"left", padding:0 }}>✕ Remover foto</button>}
        </div>
      </div>
    </div>
  );
}

function MaterialFields({form,onChange}) {
  const field = key => ({value:form[key],onChange:e=>onChange({...form,[key]:e.target.value})});
  return <div className="tool-form-grid"><Inp label="Nome *" {...field("nome")} placeholder="Ex.: ímã redondo, argola, embalagem" /><Sel label="Unidade" {...field("unidade")}>{UNIDADES.map(u=><option key={u}>{u}</option>)}</Sel><Inp label="Quantidade em estoque" type="number" min="0" {...field("quantidade")} /><Inp label="Custo por unidade (R$)" type="number" min="0" step="0.01" {...field("custo_unitario")} /><Inp label="Limite do alerta de estoque" type="number" min="0" {...field("limite_alerta")} /><label className="tool-check"><input type="checkbox" checked={form.alerta_baixo!==false} onChange={e=>onChange({...form,alerta_baixo:e.target.checked})} /> Alertar quando o estoque baixar</label><div className="tool-field-wide"><CampoFoto nome={form.nome} foto={form.foto} onChange={foto=>onChange({...form,foto})} /></div><div className="tool-field-wide"><Inp label="Observações" {...field("obs")} placeholder="Detalhes para lembrar na próxima compra" /></div></div>;
}

export function Materiais() {
  const empty = { nome:"", unidade:"un", quantidade:"", custo_unitario:"", obs:"", alerta_baixo:true, limite_alerta:10, foto:"" };
  const [saving,setSaving] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [criando, setCriando] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtroAlerta, setFiltroAlerta] = useState("Todos");
  const [detalheId, setDetalheId] = useState(null);
  const [editForm, setEditForm] = useState(empty);
  const [confirmar, modalConfirm] = useConfirm();

  const carregar = async (silencioso=true) => { if (!silencioso) setLoading(true); setData(await fbList("materiais")); if (!silencioso) setLoading(false); };
  useEffect(() => { carregar(false); }, []);

  const salvarNovo = async () => {
    if(saving)return;
    if (!form.nome) { toast("Informe o nome do material!", "warning"); return; }
    const payload = { nome:form.nome, unidade:form.unidade, quantidade:parseFloat(form.quantidade)||0, custo_unitario:parseFloat(form.custo_unitario)||null, obs:form.obs||null, alerta_baixo:form.alerta_baixo!==false, limite_alerta:parseFloat(form.limite_alerta)||10, foto:form.foto||null };
    setSaving(true);
    try {
    await fbAdd("materiais", payload);
    setForm(empty); setCriando(false); carregar();
    toast("Material adicionado!", "success");
    } catch { toast("Não foi possível salvar o material.","error"); } finally {setSaving(false);}
  };

  const abrirDetalhe = (m) => {
    setDetalheId(m.id);
    setEditForm({ nome:m.nome||"", unidade:m.unidade||"un", quantidade:m.quantidade??"", custo_unitario:m.custo_unitario??"", obs:m.obs||"", alerta_baixo:m.alerta_baixo!==false, limite_alerta:m.limite_alerta||10, foto:m.foto||"" });
  };
  const salvarEdicao = async () => {
    if(saving)return;
    if (!editForm.nome) { toast("Informe o nome do material!", "warning"); return; }
    const payload = { nome:editForm.nome, unidade:editForm.unidade, quantidade:parseFloat(editForm.quantidade)||0, custo_unitario:parseFloat(editForm.custo_unitario)||null, obs:editForm.obs||null, alerta_baixo:editForm.alerta_baixo!==false, limite_alerta:parseFloat(editForm.limite_alerta)||10, foto:editForm.foto||null };
    setSaving(true);
    try {
    await fbUpdate("materiais", detalheId, payload);
    setData(d => d.map(m => m.id===detalheId ? { ...m, ...payload } : m));
    toast("💾 Material salvo!", "success");
    setDetalheId(null);
    } catch { toast("Não foi possível salvar as alterações.","error"); } finally {setSaving(false);}
  };
  const del = async () => {
    if (!await confirmar("Excluir material?")) return;
    await fbDelete("materiais", detalheId);
    setData(d => d.filter(m => m.id!==detalheId));
    setDetalheId(null);
  };

  const baixoEstoque = (m) => (m.alerta_baixo!==false) && ((parseFloat(m.quantidade)||0) < (parseFloat(m.limite_alerta)||10));
  const dadosFiltrados = data.filter(m =>
    (!busca || m.nome?.toLowerCase().includes(busca.toLowerCase())) &&
    (filtroAlerta!=="Estoque Baixo" || baixoEstoque(m))
  );
  const detalhe = data.find(m => m.id===detalheId);

  return <div className="tool-module tool-module--materiais">
    {modalConfirm}
    <ToolSummary items={[{label:"Materiais",value:loading?"—":data.length},{label:"Estoque baixo",value:data.filter(baixoEstoque).length},{label:"Com alerta",value:data.filter(m=>m.alerta_baixo!==false).length}]} />
    <ToolSearch value={busca} onChange={setBusca} placeholder="Pesquisar material"><ToolButton onClick={()=>setCriando(true)}>+ Novo material</ToolButton></ToolSearch>
    <ToolFilters value={filtroAlerta} onChange={setFiltroAlerta} options={[{value:"Todos",label:"Todos",count:data.length},{value:"Estoque Baixo",label:"Estoque baixo",count:data.filter(baixoEstoque).length}]} />
    <div className="tool-item-grid">
      {loading?<ToolEmpty icon="materiais" title="Carregando materiais…" />:!dadosFiltrados.length?<ToolEmpty icon="materiais" title={data.length?"Nenhum material encontrado":"Tudo o que completa a sua peça"}>Cadastre acessórios, embalagens e outros insumos. O estoque fica organizado aqui.</ToolEmpty>:dadosFiltrados.map(m=><article className="tool-item" key={m.id}>
        <div className="tool-material-photo">{m.foto?<img src={m.foto} alt={m.nome} />:<HeaderIcon name="materiais" size={46} />}</div>
        <h3>{m.nome}</h3><div className="tool-item-metrics"><span>Em estoque<strong>{Number(m.quantidade||0).toLocaleString("pt-BR")} {m.unidade}</strong></span><span>Custo / unidade<strong>{m.custo_unitario!=null?Number(m.custo_unitario).toLocaleString("pt-BR",{style:"currency",currency:"BRL"}):"Não informado"}</strong></span></div>
        {baixoEstoque(m)&&<p className="tool-stock-warning">Estoque baixo · limite {m.limite_alerta||10} {m.unidade}</p>}{m.obs&&<p>{m.obs}</p>}
        <div className="tool-actions"><ToolButton secondary onClick={()=>abrirDetalhe(m)}>Ver e editar</ToolButton></div>
      </article>)}
    </div>
    {criando&&<ToolDialog compact title="Novo material" subtitle="Cadastre o insumo e defina quando deseja repor." icon="materiais" color="#edabf8" onClose={()=>setCriando(false)} busy={saving}><MaterialFields form={form} onChange={setForm} /><div className="tool-actions"><ToolButton onClick={salvarNovo} disabled={saving}>{saving?"Salvando…":"Adicionar material"}</ToolButton><ToolButton secondary disabled={saving} onClick={()=>{setCriando(false);setForm(empty);}}>Cancelar</ToolButton></div></ToolDialog>}
    {detalhe&&<ToolDialog compact title="Editar material" subtitle={detalhe.nome} icon="materiais" color="#edabf8" onClose={()=>setDetalheId(null)} busy={saving}><MaterialFields form={editForm} onChange={setEditForm} /><div className="tool-actions"><ToolButton onClick={salvarEdicao} disabled={saving}>{saving?"Salvando…":"Salvar alterações"}</ToolButton><ToolButton secondary disabled={saving} onClick={()=>setDetalheId(null)}>Cancelar</ToolButton><ToolButton danger disabled={saving} onClick={del}>Excluir material</ToolButton></div></ToolDialog>}
  </div>;
}
